import RequestError from '@app/src/errors/RequestError'
import type { Event } from '@sentry/react'
import { collectMessages, normalizeEndpoint, resolveFingerprint } from './fingerprint'
import { isChunkLoadMessage, isIgnoredMessage } from './noiseRules'

export type EventDecision<T extends Event = Event> =
  | { readonly drop: true; readonly reason: string }
  | { readonly drop: false; readonly event: T }

const reported = new WeakSet<object>()

/**
 * `useApi` reports a failure and rethrows it, and the rethrow reaches the browser's
 * unhandledrejection handler, so every failed request arrived in Sentry twice.
 * Marking the thrown object lets the second arrival be dropped.
 */
export const markAsReported = (error: unknown): void => {
  if (error && typeof error === 'object') {
    reported.add(error)
  }
}

const wasAlreadyReported = (error: unknown): boolean => !!error && typeof error === 'object' && reported.has(error)

/**
 * A response with a status code was produced by our backend, which reports its own
 * faults to the worldfavor-services project with a full server stack trace. Repeating
 * it from the browser adds a second, thinner copy of the same incident.
 *
 * 4xx are expected outcomes the UI already turns into a snackbar. 5xx are only worth
 * a browser-side event when the backend did not hand us a Sentry id for its own.
 */
export const shouldReportApiFailure = (input: { statusCode: number; hasBackendSentryId: boolean }): boolean => {
  // A successful response the client could not read is a frontend defect the backend
  // never sees, so it is the one case worth reporting from here regardless.
  if (input.statusCode >= 200 && input.statusCode < 400) {
    return true
  }

  return input.statusCode >= 500 && !input.hasBackendSentryId
}

/**
 * An API failure that escapes to the unhandledrejection handler arrives with no tags
 * at all, which is why those events used to be unfilterable and ungroupable. Lift the
 * facts off the exception so fingerprinting sees the same shape either way.
 */
const enrichFromRequestError = <T extends Event>(event: T, error: RequestError): T => ({
  ...event,
  tags: {
    ...event.tags,
    errorType: event.tags?.errorType ?? 'api',
    statusCode: String(error.statusCode),
    ...(error.request
      ? { method: error.request.method.toUpperCase(), endpoint: normalizeEndpoint(error.request.url) }
      : {}),
    ...(error.errorCode ? { errorCode: error.errorCode } : {}),
  },
})

/**
 * Only the browser's own onerror/onunhandledrejection listeners re-report an error that
 * a callsite already handled. A deliberate captureError() carries context the automatic
 * event does not, so it must never be dropped as a duplicate of one.
 */
const isFromGlobalHandler = (event: Event): boolean =>
  !!event.exception?.values?.some(value => value.mechanism?.handled === false)

export const decideEvent = <T extends Event>(incoming: T, originalException?: unknown): EventDecision<T> => {
  if (isFromGlobalHandler(incoming) && wasAlreadyReported(originalException)) {
    return { drop: true, reason: 'duplicate' }
  }

  const requestError = originalException instanceof RequestError ? originalException : undefined
  const event = requestError ? enrichFromRequestError(incoming, requestError) : incoming

  if (requestError) {
    // useApi reports every RequestError through captureApiFailure, which is also where the
    // decision to report at all is made. A callsite capturing the same error produces a
    // second event, and for a 4xx or a backend-reported 5xx it re-adds the very event
    // captureApiFailure suppressed. captureApiFailure's own event carries errorType 'api'.
    if (event.tags?.errorType !== 'api') {
      return { drop: true, reason: 'api-failure-already-owned-by-useApi' }
    }

    if (
      !shouldReportApiFailure({
        statusCode: requestError.statusCode,
        hasBackendSentryId: !!requestError.backendSentryId,
      })
    ) {
      return { drop: true, reason: 'api-failure-owned-by-backend' }
    }
  }

  const messages = collectMessages(event, originalException)

  // Chrome reports a stale-deploy chunk failure as "Failed to fetch dynamically imported
  // module", which the broad "Failed to fetch" transport rule would otherwise swallow.
  if (!messages.some(isChunkLoadMessage) && messages.some(isIgnoredMessage)) {
    return { drop: true, reason: 'known-noise' }
  }

  const fingerprint = resolveFingerprint(event, messages)

  return { drop: false, event: fingerprint ? { ...event, fingerprint: [...fingerprint] } : event }
}
